import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeamStore } from '../../store/teamStore';
import { playerCardService } from '../../services/playerCardService';
import { supabase } from '../../lib/supabase';
import type { PlayerCard, LastLevelPlayed } from '../../types/playerCard';
import { getRoleTypesForSport, getLastLevelPlayedOptions } from '../../types/playerCard';
import { ArrowLeft, Save, Upload, X, User, Award, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { INJURY_STATUS_OPTIONS, type InjuryStatus } from '../../constants/injuries';
import { uploadPlayerAvatar } from '../../utils/storageUtils';
import { PositionGroupSelector } from './PositionGroupSelector';
import { getPositionGroupsForSport } from '../../data/positionHierarchy';

export const PlayerCardForm: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const { currentTeam } = useTeamStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [card, setCard] = useState<PlayerCard | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set(['Offense', 'Defense', 'Positions']));
  const isNewPlayerCard = playerId === 'new' || !playerId;
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [pendingAvatarPreview, setPendingAvatarPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    player_id: playerId && playerId !== 'new' ? playerId : '',
    player_number: undefined as number | undefined,
    first_name: '',
    last_name: '',
    eligible_positions: [] as string[],
    role_types: [] as string[],
    injury_status: 'H' as InjuryStatus,
    health_notes: '',
    is_sub: false,
    // Football-specific preferences
    can_play_line: false,
    can_play_skill: false,
    offense_only: false,
    defense_only: false,
    // Play Requirements
    has_mpr: false,
    minimum_plays: undefined as number | undefined,
    max_plays_both_ways: undefined as number | undefined,
    // Experience
    last_level_played: undefined as LastLevelPlayed | undefined,
    // Avatar
    avatar_url: '',
    // New fields
    nickname: '',
    characteristic_trait: '',
    // Handedness/Preference (sport-specific)
    throwing_hand: undefined as 'L' | 'R' | undefined,
    batting_hand: undefined as 'L' | 'R' | 'S' | undefined,
    handedness: undefined as 'L' | 'R' | undefined,
    shooting_preference: undefined as 'L' | 'R' | undefined,
  });

  useEffect(() => {
    if (currentTeam?.id) {
      loadData();
    }
  }, [currentTeam?.id, playerId]);

  useEffect(() => {
    return () => {
      if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
    };
  }, [pendingAvatarPreview]);

  const loadData = async () => {
    if (!currentTeam?.id) return;

    setLoading(true);
    try {
      // Load players
      const { data: playersData } = await supabase
        .from('players')
        .select('*, play_participation')
        .eq('team_id', currentTeam.id)
        .order('last_name');

      setPlayers(playersData || []);

      // If editing existing card, load it
      if (playerId && playerId !== 'new') {
        const player = playersData?.find(p => p.id === playerId);
        setSelectedPlayer(player);

        const loadedCard = await playerCardService.getPlayerCardByPlayer(playerId, currentTeam.id);
        
        if (loadedCard) {
          setCard(loadedCard);
          // Load MPR data from player's play_participation
          const playParticipation = player?.play_participation as any;
          const hasMPR = playParticipation?.hasMPR || false;
          const minimumPlays = playParticipation?.minimumRequiredPlays || undefined;
          
          setFormData({
            player_id: loadedCard.player_id,
            player_number: player?.number || undefined,
            first_name: player?.first_name || '',
            last_name: player?.last_name || '',
            eligible_positions: loadedCard.eligible_positions || [],
            role_types: loadedCard.role_types || [],
            injury_status: (player?.injury_status as InjuryStatus) || 'H',
            health_notes: loadedCard.health_notes || '',
            is_sub: (player as any)?.is_sub ?? false,
            can_play_line: loadedCard.can_play_line || false,
            can_play_skill: loadedCard.can_play_skill || false,
            offense_only: loadedCard.offense_only || false,
            defense_only: loadedCard.defense_only || false,
            max_plays_both_ways: loadedCard.max_plays_both_ways,
            last_level_played: (player?.experience as LastLevelPlayed) || (loadedCard.last_level_played as LastLevelPlayed) || undefined,
            has_mpr: hasMPR,
            minimum_plays: minimumPlays,
            avatar_url: loadedCard.avatar_url || loadedCard.photo_url || '',
            nickname: loadedCard.nickname || '',
            characteristic_trait: loadedCard.characteristic_trait || '',
            throwing_hand: loadedCard.throwing_hand as 'L' | 'R' | undefined,
            // Sync batting_hand from player table if not in card (for backward compatibility)
            batting_hand: (loadedCard.batting_hand || player?.batting_hand) as 'L' | 'R' | 'S' | undefined,
            handedness: loadedCard.handedness as 'L' | 'R' | undefined,
            shooting_preference: loadedCard.shooting_preference as 'L' | 'R' | undefined,
          });
        } else if (player) {
          // Load MPR data from player's play_participation for new cards too
          const playParticipation = player?.play_participation as any;
          const hasMPR = playParticipation?.hasMPR || false;
          const minimumPlays = playParticipation?.minimumRequiredPlays || undefined;
          
          setFormData(prev => ({
            ...prev,
            player_number: player.number || undefined,
            injury_status: (player.injury_status as InjuryStatus) || 'H',
            has_mpr: hasMPR,
            minimum_plays: minimumPlays,
            is_sub: (player as any)?.is_sub ?? false,
            last_level_played: (player.experience as LastLevelPlayed) || undefined,
          }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentTeam?.id) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    event.target.value = '';

    if (!formData.player_id) {
      if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
      setPendingAvatarFile(file);
      setPendingAvatarPreview(URL.createObjectURL(file));
      return;
    }

    setUploadingAvatar(true);
    try {
      const publicUrl = await uploadPlayerAvatar(file, formData.player_id, currentTeam.id);
      setFormData(prev => ({ ...prev, avatar_url: publicUrl }));
      toast.success('Avatar uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      toast.error(error?.message || 'Failed to upload avatar', { duration: 6000 });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = () => {
    if (pendingAvatarFile || pendingAvatarPreview) {
      if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
      setPendingAvatarFile(null);
      setPendingAvatarPreview(null);
    } else {
      setFormData(prev => ({ ...prev, avatar_url: '' }));
    }
  };

  const handleSave = async () => {
    if (!currentTeam?.id) return;

    let playerIdToUse = formData.player_id;

    // Creating new player: insert into players first
    if (isNewPlayerCard) {
      const firstName = (formData.first_name || '').trim();
      const lastName = (formData.last_name || '').trim();
      if (!firstName || !lastName) {
        toast.error('Please enter first name and last name');
        return;
      }
      setSaving(true);
      try {
        const { data: newPlayer, error: insertError } = await supabase
          .from('players')
          .insert({
            team_id: currentTeam.id,
            first_name: firstName,
            last_name: lastName,
            number: formData.player_number ?? null,
            position: '--',
            experience: formData.last_level_played || 'Beginner',
            injury_status: formData.injury_status,
            is_sub: formData.is_sub ?? false,
          })
          .select('id')
          .single();

        if (insertError || !newPlayer?.id) {
          console.error('Error creating player:', insertError);
          toast.error(insertError?.message || 'Failed to create player');
          setSaving(false);
          return;
        }
        playerIdToUse = newPlayer.id;
      } catch (err) {
        console.error(err);
        toast.error('Failed to create player');
        setSaving(false);
        return;
      }
    } else {
      const firstName = (formData.first_name || '').trim();
      const lastName = (formData.last_name || '').trim();
      if (!firstName || !lastName) {
        toast.error('Please enter first name and last name');
        return;
      }
    }
    if (!formData.player_id && !isNewPlayerCard) {
      toast.error('Please select a player');
      return;
    }

    if (roleTypes.length > 0 && formData.role_types.length === 0) {
      toast.error('Please select at least one role type');
      return;
    }

    setSaving(true);
    try {
      const card = await playerCardService.getOrCreatePlayerCard(playerIdToUse, currentTeam.id);

      let avatarUrl = formData.avatar_url || undefined;
      if (pendingAvatarFile && currentTeam?.id) {
        avatarUrl = await uploadPlayerAvatar(pendingAvatarFile, playerIdToUse, currentTeam.id);
        if (pendingAvatarPreview) URL.revokeObjectURL(pendingAvatarPreview);
        setPendingAvatarFile(null);
        setPendingAvatarPreview(null);
      }
      
      // Update player card
      await playerCardService.updatePlayerCard(card.id, {
        eligible_positions: formData.eligible_positions,
        role_types: formData.role_types,
        health_notes: formData.health_notes || undefined,
        can_play_line: formData.can_play_line,
        can_play_skill: formData.can_play_skill,
        offense_only: formData.offense_only,
        defense_only: formData.defense_only,
        max_plays_both_ways: formData.max_plays_both_ways,
        avatar_url: avatarUrl,
        nickname: formData.nickname || undefined,
        characteristic_trait: formData.characteristic_trait || undefined,
        throwing_hand: formData.throwing_hand || undefined,
        batting_hand: formData.batting_hand || undefined,
        handedness: formData.handedness || undefined,
        shooting_preference: formData.shooting_preference || undefined,
        last_level_played: formData.last_level_played ?? undefined,
      });

      // Update player's number, injury status, MPR, etc. (stored on the players table)
      if (playerIdToUse) {
        const { data: existingPlayer } = await supabase
          .from('players')
          .select('play_participation')
          .eq('id', playerIdToUse)
          .single();

        const existingPlayParticipation = (existingPlayer?.play_participation as any) || {};
        
        // Update play_participation with MPR data
        const updatedPlayParticipation = {
          ...existingPlayParticipation,
          hasMPR: formData.has_mpr || false,
          minimumRequiredPlays: formData.has_mpr ? (formData.minimum_plays || 0) : 0,
          priorityLevel: formData.has_mpr ? 2 : 1, // MPR players are level 2, starters are level 1
          playsLogged: existingPlayParticipation.playsLogged || 0,
          forceInsert: existingPlayParticipation.forceInsert || false,
          isAvailable: existingPlayParticipation.isAvailable !== undefined ? existingPlayParticipation.isAvailable : true,
        };

        const firstName = (formData.first_name || '').trim();
        const lastName = (formData.last_name || '').trim();
        const updateData: any = {
          injury_status: formData.injury_status,
          play_participation: updatedPlayParticipation,
          is_sub: formData.is_sub ?? false,
          experience: formData.last_level_played || 'Beginner',
          ...(firstName && { first_name: firstName }),
          ...(lastName && { last_name: lastName }),
        };

        if (formData.player_number !== undefined) {
          updateData.number = formData.player_number || null;
        }

        const { error: playerError } = await supabase
          .from('players')
          .update(updateData)
          .eq('id', playerIdToUse);

        if (playerError) {
          console.error('Error updating player data:', playerError);
          // Don't throw - card was saved successfully
        }
      }

      toast.success('Player card saved successfully');
      navigate(`/player-cards/${playerIdToUse}`);
    } catch (error) {
      console.error('Error saving player card:', error);
      toast.error('Failed to save player card');
    } finally {
      setSaving(false);
    }
  };

  const togglePosition = (position: string) => {
    setFormData(prev => ({
      ...prev,
      eligible_positions: prev.eligible_positions.includes(position)
        ? prev.eligible_positions.filter(p => p !== position)
        : [...prev.eligible_positions, position],
    }));
  };

  const toggleRoleType = (roleType: string) => {
    setFormData(prev => ({
      ...prev,
      role_types: prev.role_types.includes(roleType)
        ? prev.role_types.filter(r => r !== roleType)
        : [...prev.role_types, roleType],
    }));
  };

  // Update selected player when player_id changes
  useEffect(() => {
    if (formData.player_id && players.length > 0) {
      const player = players.find(p => p.id === formData.player_id);
      setSelectedPlayer(player);
    }
  }, [formData.player_id, players]);

  const roleTypes = currentTeam ? getRoleTypesForSport(currentTeam.sport) : [];
  const lastLevelPlayedOptions = getLastLevelPlayedOptions(currentTeam?.sport);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  const toggleUnit = (unit: string) => {
    setExpandedUnits(prev => {
      const next = new Set(prev);
      if (next.has(unit)) {
        next.delete(unit);
      } else {
        next.add(unit);
      }
      return next;
    });
  };

  const optionBtn = (value: string, _label: string, current: string | undefined) =>
    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ' +
    (current === value ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200');

  const formatRoleTypeLabel = (role: string): string =>
    role
      .split(/[_\-\s]+/g)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const orderedRoleTypes = (() => {
    if (!currentTeam?.sport) return roleTypes;
    if (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball') {
      // Put Battery last for baseball/softball
      const withoutBattery = roleTypes.filter((r) => r !== 'battery');
      return roleTypes.includes('battery') ? [...withoutBattery, 'battery'] : withoutBattery;
    }
    return roleTypes;
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/players')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {playerId ? 'Edit Player' : 'Create Player'}
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
        {/* Top header: Photo | Name, Jersey #, then positions below (same look as before, at top) */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
          <div className="relative group flex-shrink-0">
            <button
              type="button"
              onClick={() => !uploadingAvatar && fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={formData.avatar_url || pendingAvatarPreview ? 'Change photo' : 'Upload photo'}
            >
              {(formData.avatar_url || pendingAvatarPreview) ? (
                <img src={formData.avatar_url || pendingAvatarPreview || ''} alt="Player" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-primary bg-opacity-20 flex items-center justify-center">
                  <User className="text-primary" size={32} />
                </div>
              )}
              <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                <Upload className="text-white" size={20} aria-hidden />
              </span>
            </button>
            {(formData.avatar_url || pendingAvatarPreview) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemoveAvatar(); }}
                className="absolute -top-0.5 -right-0.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow"
                aria-label="Remove photo"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">
                {formData.nickname ? `"${formData.nickname}" ` : ''}
                {selectedPlayer ? `${selectedPlayer.first_name} ${selectedPlayer.last_name}` : `${formData.first_name} ${formData.last_name}`.trim() || 'New player'}
              </h3>
              {(formData.player_number !== undefined ? formData.player_number : selectedPlayer?.number) != null && (
                <span className="text-sm text-gray-500">#{formData.player_number !== undefined ? formData.player_number : selectedPlayer?.number}</span>
              )}
            </div>
            {formData.characteristic_trait && (
              <p className="text-sm text-primary font-medium mt-1">
                {formData.characteristic_trait}
              </p>
            )}
            {formData.eligible_positions.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {formData.eligible_positions.map((pos) => (
                  <span
                    key={pos}
                    className="px-2 py-0.5 bg-primary bg-opacity-10 text-primary rounded text-xs font-medium"
                  >
                    {pos}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 mt-1">No positions set</p>
            )}
          </div>
        </div>

        {/* Player basics: same fields for create and edit */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            {/* Left column: First name, then # */}
            <div className="min-w-0 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">#</label>
                <input
                  type="number"
                  min={0}
                  max={999}
                  value={formData.player_number ?? ''}
                  onChange={(e) => setFormData({ ...formData, player_number: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
                  className="w-full max-w-[6rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="—"
                />
              </div>
            </div>
            {/* Right column: Last name, then Sub */}
            <div className="min-w-0 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sub</label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_sub}
                  onClick={() => setFormData({ ...formData, is_sub: !formData.is_sub })}
                  className={`relative inline-flex h-9 w-24 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${formData.is_sub ? 'bg-green-500 focus:ring-green-500' : 'bg-red-500 focus:ring-red-500'}`}
                >
                  <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold transition-colors ${formData.is_sub ? 'text-white/70' : 'text-white'}`}>Yes</span>
                  <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold transition-colors ${formData.is_sub ? 'text-white' : 'text-white/70'}`}>No</span>
                  <span className={`absolute top-1 bottom-1 w-11 rounded-full bg-white shadow-md transition-all duration-200 ease-out ${formData.is_sub ? 'right-1 left-auto' : 'left-1 right-auto'}`} aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Nickname */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
          <input type="text" value={formData.nickname} onChange={(e) => setFormData({ ...formData, nickname: e.target.value })} className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Optional" maxLength={30} />
        </div>

        {/* Role Types (required when sport has role types) */}
        {roleTypes.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Role Types *</label>
            <div className="flex flex-wrap gap-1.5">
              {orderedRoleTypes.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => toggleRoleType(role)}
                  className={`px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                    formData.role_types.includes(role)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {formatRoleTypeLabel(role)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Last Level Played */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Level Played</label>
          <select
            value={lastLevelPlayedOptions.includes(formData.last_level_played as string) ? formData.last_level_played : ''}
            onChange={(e) => setFormData({ ...formData, last_level_played: (e.target.value || undefined) as LastLevelPlayed | undefined })}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">Select level...</option>
            {lastLevelPlayedOptions.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        {/* Baseball/Softball: Throwing | Batting — 2 columns, label above buttons */}
        {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (
          <div className="grid grid-cols-2 gap-4 max-w-sm">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Throwing</label>
              <div className="flex gap-1.5">
                {(['L', 'R'] as const).map((h) => (
                  <button key={h} type="button" onClick={() => setFormData({ ...formData, throwing_hand: formData.throwing_hand === h ? undefined : h })} className={optionBtn(h, h, formData.throwing_hand)}>{h}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Batting</label>
              <div className="flex gap-1.5">
                {(['L', 'R', 'S'] as const).map((h) => (
                  <button key={h} type="button" onClick={() => setFormData({ ...formData, batting_hand: formData.batting_hand === h ? undefined : h })} className={optionBtn(h, h === 'S' ? 'Switch' : h, formData.batting_hand)}>{h === 'S' ? 'S' : h}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Soccer/Hockey: Shooting L/R buttons */}
        {(currentTeam?.sport === 'Soccer' || currentTeam?.sport === 'Hockey') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Shooting</label>
            <div className="flex gap-2">
              {(['L', 'R'] as const).map((h) => (
                <button key={h} type="button" onClick={() => setFormData({ ...formData, shooting_preference: formData.shooting_preference === h ? undefined : h })} className={optionBtn(h, h, formData.shooting_preference)}>{h}</button>
              ))}
            </div>
          </div>
        )}

        {/* Other Sports: Handedness L/R buttons */}
        {currentTeam?.sport && currentTeam.sport !== 'Baseball' && currentTeam.sport !== 'Softball' && currentTeam.sport !== 'Soccer' && currentTeam.sport !== 'Hockey' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Handedness</label>
            <div className="flex gap-2">
              {(['L', 'R'] as const).map((h) => (
                <button key={h} type="button" onClick={() => setFormData({ ...formData, handedness: formData.handedness === h ? undefined : h })} className={optionBtn(h, h, formData.handedness)}>{h}</button>
              ))}
            </div>
          </div>
        )}

        {/* Position groups: same UI for all sports (Football, Flag Football, Basketball, Soccer, etc.) */}
        {currentTeam?.sport && getPositionGroupsForSport(currentTeam.sport) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position Groups</label>
            <div className="space-y-2">
              {getPositionGroupsForSport(currentTeam.sport)!.units.map(({ unit }) => (
                <div key={unit} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleUnit(unit)}
                    className="w-full bg-gray-100 px-3 py-1.5 flex items-center justify-between hover:bg-gray-200 transition-colors text-sm"
                  >
                    <span className="font-medium text-gray-900">{unit}</span>
                    {expandedUnits.has(unit) ? (
                      <ChevronDown size={18} className="text-gray-600" />
                    ) : (
                      <ChevronRight size={18} className="text-gray-600" />
                    )}
                  </button>
                  {expandedUnits.has(unit) && (
                    <div className="p-3 bg-white">
                      <PositionGroupSelector
                        sport={currentTeam.sport}
                        unit={unit}
                        selectedGroups={formData.eligible_positions}
                        onGroupToggle={togglePosition}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Injury Status — H (green), Q (yellow), D (orange), O (red) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Injury</label>
          <div className="flex flex-wrap gap-1.5">
            {INJURY_STATUS_OPTIONS.map((opt) => {
              const isSelected = formData.injury_status === opt.value;
              const colorClass = isSelected
                ? opt.value === 'H'
                  ? 'bg-green-500 text-white'
                  : opt.value === 'Q'
                    ? 'bg-yellow-500 text-white'
                    : opt.value === 'D'
                      ? 'bg-orange-500 text-white'
                      : 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, injury_status: opt.value })}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${colorClass}`}
                >
                  {opt.value === 'H' ? 'H' : opt.value === 'Q' ? 'Q' : opt.value === 'D' ? 'D' : 'O'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Health Notes — full width, larger text area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Health Notes</label>
          <textarea value={formData.health_notes} onChange={(e) => setFormData({ ...formData, health_notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm" rows={4} placeholder="Optional..." />
        </div>

        {/* Football-Specific Preferences */}
        {currentTeam?.sport === 'Football' && (
          <div className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <Award size={16} /> Football
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.can_play_line}
                  onChange={(e) => setFormData({ ...formData, can_play_line: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Can Play Line</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.can_play_skill}
                  onChange={(e) => setFormData({ ...formData, can_play_skill: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Can Play Skill</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.offense_only}
                  onChange={(e) => setFormData({ ...formData, offense_only: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Offense Only</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.defense_only}
                  onChange={(e) => setFormData({ ...formData, defense_only: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Defense Only</span>
              </label>
            </div>
          </div>
        )}

        {/* Play Requirements */}
        <div className="border-t pt-4 space-y-3">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={formData.has_mpr} onChange={(e) => setFormData({ ...formData, has_mpr: e.target.checked })} className="rounded border-gray-300 text-primary focus:ring-primary" />
            <span className="text-sm font-medium text-gray-700">MPR (min plays)</span>
          </label>
          {formData.has_mpr && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Min plays</label>
                <input type="number" min={1} value={formData.minimum_plays ?? ''} onChange={(e) => setFormData({ ...formData, minimum_plays: e.target.value ? parseInt(e.target.value, 10) : undefined })} className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="8" />
              </div>
              {currentTeam?.sport === 'Football' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Max both ways</label>
                  <input type="number" min={0} value={formData.max_plays_both_ways ?? ''} onChange={(e) => setFormData({ ...formData, max_plays_both_ways: e.target.value ? parseInt(e.target.value, 10) : undefined })} className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="—" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* OVR Rating Section */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
            <Award size={18} />
            Overall Rating (OVR)
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">
              OVR is calculated from attributes (Physical, Technical, Mental) on a 0-100 scale (Madden-style).
              Set up attributes in the <strong>Attributes</strong> tab to see the OVR rating.
            </p>
            {formData.player_id && card?.ovr_rating !== undefined ? (
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">Overall Rating</p>
                <div className="text-4xl font-bold text-primary">
                  {card.ovr_rating}
                  <span className="text-xl text-gray-500">/100</span>
                </div>
                {card.rating_breakdown && (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-gray-500">Physical</div>
                      <div className="font-semibold">{card.rating_breakdown.physical_score.toFixed(1)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Technical</div>
                      <div className="font-semibold">{card.rating_breakdown.technical_score.toFixed(1)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Mental</div>
                      <div className="font-semibold">{card.rating_breakdown.mental_score.toFixed(1)}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center">
                {formData.player_id 
                  ? 'Set up attributes on the player card detail page to see OVR rating'
                  : 'Save the player card first, then set up attributes to see OVR rating'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
